import React, { useState, useEffect } from "react";
import GridMaster from "../masters/GridMaster";
import ActionRenderer from "../masters/ActionRenderer";
import { formatDateInDDMMYYYY } from "../../functions/common-functions";
import { encryptData } from "../../utils/encryption";
import { getWorkflowHistory } from "../../services/disposal-intimation-service";

const PendingApprovalDashboard = ({ data, resetFilter, setAction, setShowForm, setRowData }) => {
  const [datafilter, setDatafilter] = useState([])

  const handleEdit = (row) => {
    setRowData(row);
    setAction("Approval");
    setShowForm(true);
  };

  const fetch = async () => {
    const allWorkflowData = [];
    for (let i = 0; i < data.length; i++) {
      const workflowHistory = await getWorkflowHistory(data[i].IntimationNumber);
      allWorkflowData.push(...workflowHistory);
    }
    setDatafilter(allWorkflowData);
  }

  useEffect(() => {
    if (data && data.length > 0) {
      fetch();
    }
  }, [data])

  useEffect(() => {
    if (data && data.length > 0) {
      fetch();
    }
  }, [data])

  const calculateAging = (row) => {
    const currentDate = new Date();
    let referenceDate;

    if (row.Status === "Pending for Final Approval") {
      const filterdata = datafilter.filter((item) => item.Status === row.Status);
      let latestEntry = filterdata[0];
      for (let i = 1; i < filterdata.length; i++) {
        if (new Date(filterdata[i].ActionDate) > new Date(latestEntry.ActionDate)) {
          latestEntry = filterdata[i];
        }
      }
      referenceDate = new Date(latestEntry?.ActionDate);
    } else {
      referenceDate = new Date(row.IntimationDate);
    }

    const diffTime = Math.abs(currentDate - referenceDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getRowStyle = (params) => {
    const aging = calculateAging(params.data);

    if (aging > 30) {
      return { background: '#ffcccc' };
    } else if (aging > 15) {
      return { background: '#ccffcc' };
    } else if (aging > 7) {
      return { background: '#cce5ff' };
    }
    return null;
  };

  const columnDefs = [
    {
      headerName: "Disposal Intimation Number",
      field: "IntimationNumber",
      minWidth: 180,
      tooltipField: "IntimationNumber",
      sort: 'asc'
    },
    {
      headerName: "Date",
      field: "IntimationDate",
      minWidth: 100,
      tooltipField: "IntimationDate",
      valueFormatter: (params) => {
        return params.value ? formatDateInDDMMYYYY(params.value.toString()) : "";
      },
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          if (cellValue == null) return 0;
          const dateCell = new Date(cellValue);
          dateCell.setHours(0, 0, 0, 0);
          if (dateCell < filterLocalDateAtMidnight) return -1;
          if (dateCell > filterLocalDateAtMidnight) return 1;
          return 0;
        },
      },
    },
    {
      headerName: "Initiator Name",
      field: "InitiatorName",
      minWidth: 150,
      tooltipField: "InitiatorName",
    },
    {
      headerName: "Division",
      field: "InitiatorDivision",
      minWidth: 120,
      tooltipField: "InitiatorDivision",
    },
    {
      headerName: "Location",
      field: "InitiatorLocation",
      minWidth: 120,
      valueGetter: (params) => {
        return params.data.InitiatorLocation === "Other" && params.data.IfOtherLoc
          ? params.data.IfOtherLoc
          : params.data.InitiatorLocation || "";
      },
      tooltipValueGetter: (params) => {
        return params.data.InitiatorLocation === "Other" && params.data.IfOtherLoc
          ? params.data.IfOtherLoc
          : params.data.InitiatorLocation || "";
      }
    },
    {
      headerName: "Item Code",
      field: "ItemCode",
      minWidth: 120,
      tooltipField: "ItemCode",
    },
    {
      headerName: "Item Description",
      field: "ItemDescription",
      minWidth: 150,
      tooltipField: "ItemDescription",
    },
    {
      headerName: "Status",
      field: "Status",
      minWidth: 180,
      tooltipField: "Status",
    },
    {
      headerName: "Pending with",
      field: "PendingWith",
      minWidth: 150,
      tooltipValueGetter: (params) => {
        if (Array.isArray(params.value)) {
          const decryptedNames = params.value.map(name => encryptData(name, "dec"));
          return decryptedNames.join(", ");
        }
        return params.value ? encryptData(params.value, "dec") : "";
      },
      valueFormatter: (params) => {
        if (Array.isArray(params.value)) {
          const decryptedNames = params.value.map(name => encryptData(name, "dec"));
          return decryptedNames.join(", ");
        }
        return params.value ? encryptData(params.value, "dec") : "";
      }
    },
    {
      headerName: "",
      field: "",
      maxWidth: 100,
      cellRenderer: (params) => (
        <ActionRenderer
          selectedData={params.data}
          handleEditEvent={() => handleEdit(params.data)}
          showEdit={true}
          showView={false}
          showDelete={false}
        />
      ),
      sortable: false,
      filter: false,
      pinned: "right",
      resizable: false,
    }
  ];

  const headerName = (field) => {
    const mapping = {
      "IntimationNumber": "Disposal Intimation Number",
      "IntimationDate": "Date",
      "InitiatorName": "Initiator Name",
      "InitiatorDivision": "Division",
      "InitiatorLocation": "Location",
      "ItemCode": "Item Code",
      "ItemDescription": "Item Description",
      "Status": "Status",
      "PendingWith": "Pending with"
    };
    return mapping[field] || "No match found";
  };

  const fieldName = (header) => {
    const mapping = {
      "Disposal Intimation Number": "IntimationNumber",
      "Date": "IntimationDate",
      "Initiator Name": "InitiatorName",
      "Division": "InitiatorDivision",
      "Location": "InitiatorLocation",
      "Item Code": "ItemCode",
      "Item Description": "ItemDescription",
      "Status": "Status",
      "Pending with": "PendingWith"
    };
    return mapping[header] || "No match found";
  };

  return (
    <div className="dashboard-panel">
      <h5 className="panel-title"><span>&nbsp;&nbsp;&nbsp;</span>Requests Pending for your Approval</h5>
      <GridMaster
        rowDetails={data}
        colDetails={columnDefs}
        fieldNames={fieldName}
        headerNames={headerName}
        getDataEvent={() => { }}
        getFilterColLength={() => { }}
        setExcelData={() => { }}
        resetind={resetFilter}
        getRowStyle={getRowStyle}
      />

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '20px' }}>
        <span>&nbsp;&nbsp;&nbsp;</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '20px', backgroundColor: '#ffffff', border: '1px solid #ccc' }}></div>
          <span>1-7 days</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '20px', backgroundColor: '#cce5ff' }}></div>
          <span>8-14 days</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '20px', backgroundColor: '#ccffcc' }}></div>
          <span>15-30 days</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '20px', backgroundColor: '#ffcccc' }}></div>
          <span>&gt;30 days</span>
        </div>
      </div>

      <div><span>&nbsp;&nbsp;&nbsp;</span><b>** Only pending requests are visible. Deleted and Rejected are not shown.</b></div>
    </div>
  );
};

export default PendingApprovalDashboard;
